import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Vehicle } from 'types/vehicle';
import { EASYPAY_REQUEST_BUY_FEE_METHODS, FINALIZE_BUY_FEE_METHODS } from 'constants/index';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BlockTitle = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #000;
  margin-bottom: 30px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
`;

const SectionTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #000;
`;

const SectionValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #000;
`;

const PaymentMethodLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #000;
  margin-right: 4px;
`;

const PaidLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #000;
`;

const PaidDate = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #666666;
  margin-left: 4px;
`;

const PaidAmount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #000;
`;

const RemainingValue = styled.span<{ overpaid?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ overpaid }) => (overpaid ? '#2e7d32' : '#af2930')};
`;

const TotalPaidRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
`;

const TotalPaidValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #225a91;
`;

const formatAmount = (amount: number, negative = false) => {
  const formatted = `$${Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
  return negative ? `-${formatted}` : formatted;
};

interface PaymentHistoryBlockProps {
  vehicle: Vehicle & { payment_method?: string };
}

const PaymentHistoryBlock = ({ vehicle }: PaymentHistoryBlockProps) => {
  const feeWasPaidByCard =
    vehicle.buy_fee?.request_buy_fee_method === EASYPAY_REQUEST_BUY_FEE_METHODS.PAY_WITH_CREDIT_CARD ||
    vehicle.buy_fee?.finalize_buy_fee_method === FINALIZE_BUY_FEE_METHODS.CREDIT_CARD;
  const paymentMethodLabel = feeWasPaidByCard
    ? `${vehicle.serialized_payment_type}/Card`
    : vehicle.serialized_payment_type;

  const moneyFlow = vehicle.money_flow ?? [];
  const totalPaid = moneyFlow.reduce((sum, p) => sum + Number(p.amount), 0);

  const vehiclePrice = vehicle.vehicle_price ?? 0;
  const buyFee = vehicle.buy_fee?.processing_fee ?? 0;
  const transportationFee = vehicle.buy_fee?.transportation_fee ?? 0;
  const additionalFeesTotal = (vehicle.additional_fees ?? []).reduce(
    (sum: number, f: any) => sum + Number(f.fee_amount),
    0
  );
  const totalPrice = vehiclePrice + buyFee + transportationFee + additionalFeesTotal;
  const remaining = totalPrice - totalPaid;

  return (
    <Wrapper>
      <BlockTitle>Payment History</BlockTitle>
      <Row>
        <SectionTitle>Payment method</SectionTitle>
        <SectionValue>{paymentMethodLabel}</SectionValue>
      </Row>

      {moneyFlow.map(record => (
        <Row key={record.id}>
          <span>
            {record.payment_method && <PaymentMethodLabel>{record.payment_method}</PaymentMethodLabel>}
            <PaidLabel>Paid</PaidLabel>
            <PaidDate>{moment(record.date_received).format('MMM DD, YYYY')}</PaidDate>
          </span>
          <PaidAmount>{formatAmount(record.amount, true)}</PaidAmount>
        </Row>
      ))}

      <Row>
        <SectionTitle>{remaining < 0 ? 'Overpaid' : 'Remaining'}</SectionTitle>
        <RemainingValue overpaid={remaining < 0}>{formatAmount(remaining)}</RemainingValue>
      </Row>

      <TotalPaidRow>
        <TotalPaidValue>Total Paid {formatAmount(totalPaid)}</TotalPaidValue>
      </TotalPaidRow>
    </Wrapper>
  );
};

export default PaymentHistoryBlock;
